import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getConsent, setConsent, ALLOW_ALL, DENY_ALL } from '../lib/consent';

/*
  Cookie consent banner.

  EU-compliant shape: reject is exactly as easy as accept (same size, same
  weight, side by side), nothing is pre-ticked, and the choice can be reopened
  later from the footer. Nothing non-essential loads until a button is pressed.
*/

// Stored verbatim with the decision so we can show what was agreed to.
const WORDING =
  'We use strictly necessary cookies to run this site. With your permission we also use analytics cookies to understand how the site is used, and marketing cookies to measure our campaigns.';

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // Only show when no valid decision exists.
    if (!getConsent()) setOpen(true);
    const onReset = (e) => { if (!e.detail) { setOpen(true); setDetail(false); } };
    window.addEventListener('eagentix:consent', onReset);
    return () => window.removeEventListener('eagentix:consent', onReset);
  }, []);

  if (!open) return null;

  const decide = (choice) => {
    setConsent(choice, WORDING);
    setOpen(false);
  };

  const btn = {
    flex: '1 1 auto', minHeight: '46px', padding: '0 22px', borderRadius: '10px',
    fontSize: '0.95rem', fontWeight: 800, cursor: 'pointer', border: '1px solid transparent',
    fontFamily: 'inherit', whiteSpace: 'nowrap',
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cc-title"
      style={{
        position: 'fixed', left: '16px', right: '16px', bottom: '16px', zIndex: 2147483000,
        maxWidth: '620px', margin: '0 auto', padding: '22px 24px',
        background: '#0f172a', color: '#e2e8f0', border: '1px solid rgba(148,163,184,.3)',
        borderRadius: '16px', boxShadow: '0 24px 60px rgba(0,0,0,.45)',
        fontFamily: 'inherit',
      }}
    >
      <h2 id="cc-title" style={{ margin: '0 0 8px', fontSize: '1.05rem', fontWeight: 800, color: '#ffffff' }}>
        Cookies on eagentix.com
      </h2>
      <p style={{ margin: '0 0 16px', fontSize: '0.92rem', lineHeight: 1.6, color: '#cbd5e1' }}>
        {WORDING}{' '}
        <Link to="/privacy-policy" style={{ color: '#93c5fd', textDecoration: 'underline' }}>
          Privacy Policy
        </Link>
      </p>

      {detail && (
        <div style={{ margin: '0 0 16px', padding: '14px 16px', background: 'rgba(148,163,184,.1)', borderRadius: '10px' }}>
          <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px', color: '#94a3b8', fontSize: '0.88rem' }}>
            <input type="checkbox" checked disabled style={{ marginTop: '3px' }} />
            <span><b style={{ color: '#e2e8f0' }}>Strictly necessary</b> — security and session. Always on; the site cannot run without them.</span>
          </label>
          <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px', color: '#cbd5e1', fontSize: '0.88rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} style={{ marginTop: '3px' }} />
            <span><b style={{ color: '#e2e8f0' }}>Analytics</b> — Google Analytics, so we can see which pages are useful.</span>
          </label>
          <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', color: '#cbd5e1', fontSize: '0.88rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} style={{ marginTop: '3px' }} />
            <span><b style={{ color: '#e2e8f0' }}>Marketing</b> — LinkedIn and Google Ads measurement of our campaigns.</span>
          </label>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        {/* Reject is deliberately identical in size and weight to Accept.
            A banner where refusing is harder is not valid consent under GDPR. */}
        <button type="button" onClick={() => decide(DENY_ALL)}
          style={{ ...btn, background: 'transparent', color: '#e2e8f0', borderColor: 'rgba(148,163,184,.5)' }}>
          Reject all
        </button>

        {detail ? (
          <button type="button" onClick={() => decide({ analytics, marketing })}
            style={{ ...btn, background: '#334155', color: '#ffffff' }}>
            Save choices
          </button>
        ) : (
          <button type="button" onClick={() => setDetail(true)}
            style={{ ...btn, background: 'transparent', color: '#93c5fd', borderColor: 'rgba(148,163,184,.5)' }}>
            Manage
          </button>
        )}

        <button type="button" onClick={() => decide(ALLOW_ALL)}
          style={{ ...btn, background: '#2563eb', color: '#ffffff' }}>
          Accept all
        </button>
      </div>
    </div>
  );
}
